import { film, athlete } from "@/data/athlete";
import { SectionHeader, PlayBadge } from "./_shared";

export const Film = () => {
  const tag = `#${athlete.jerseyNumber}`;
  return (
    <section
      id="film"
      data-testid="film-section"
      className="border-t border-zinc-900 py-16 md:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div
          data-testid="film-position-tag"
          className="mb-4 text-xs uppercase tracking-[0.25em] font-bold text-zinc-500"
        >
          {athlete.position} • #{athlete.jerseyNumber} • Class of{" "}
          {athlete.gradYear}
        </div>
        <SectionHeader
          eyebrow="01 — Film Room"
          title="Highlights & Game Film"
          kicker="Verified game footage, showcases, and full mixtapes."
          testId="film-header"
        />

        {/* Print-only film list (replaces video tiles on PDF) */}
        <div data-testid="film-print-list" className="print-only mt-6 space-y-2">
          <p className="text-base">
            <strong>Highlight Film:</strong>{" "}
            <a href={film.highlight.url} className="underline">
              {film.highlight.title}
            </a>
          </p>
          {film.clips.map((clip) => (
            <p key={clip.title} className="text-base">
              <strong>
                {clip.title.toLowerCase().startsWith("full game")
                  ? "Full Game Film"
                  : clip.title}
                :
              </strong>{" "}
              <a href={clip.url} className="underline">
                {clip.title}
              </a>
            </p>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-5 no-print">
          {/* Highlight reel — large */}
          <a
            data-testid="film-highlight"
            href={film.highlight.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(ev) => {
              ev.preventDefault();
              window.open(film.highlight.url, "_blank", "noopener,noreferrer");
            }}
            className="group relative lg:col-span-8 aspect-video overflow-hidden border border-zinc-800 bg-[#141414] cursor-pointer"
          >
            <img
              src={film.highlight.thumbnail}
              alt={`${tag} – ${film.highlight.title}`}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
            <PlayBadge duration={film.highlight.duration} />
            <div className="absolute left-5 bottom-5 right-5">
              <div
                data-testid="film-highlight-tag"
                className="inline-block bg-[#FF3B30] text-white text-[10px] md:text-xs uppercase tracking-[0.25em] font-bold px-2 py-1 mb-3"
              >
                Primary Highlight
              </div>
              <div
                data-testid="film-highlight-title"
                className="font-display text-2xl md:text-4xl text-white uppercase tracking-tight"
              >
                {tag} – {film.highlight.title}
              </div>
            </div>
          </a>

          {/* Side clips */}
          <div className="lg:col-span-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 md:gap-5">
            {film.clips.map((clip, idx) => {
              const isExternal =
                clip.url && clip.url !== "#" && /^https?:\/\//.test(clip.url);
              return (
                <a
                  key={clip.title}
                  data-testid={`film-clip-${idx}`}
                  href={clip.url}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noopener noreferrer" : undefined}
                  onClick={
                    isExternal
                      ? (ev) => {
                          ev.preventDefault();
                          window.open(
                            clip.url,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      : undefined
                  }
                  className="group relative aspect-video overflow-hidden border border-zinc-800 bg-[#141414] cursor-pointer"
                >
                  <img
                    src={clip.thumbnail}
                    alt={`${tag} – ${clip.title}`}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <PlayBadge duration={clip.duration} />
                  <div className="absolute left-4 bottom-3 right-4">
                    <div
                      data-testid={`film-clip-${idx}-title`}
                      className="font-bold text-sm md:text-base text-white leading-snug"
                    >
                      {clip.cta ? (
                        <>
                          {clip.title}
                          <span className="text-[#FF3B30]"> — {clip.cta}</span>
                        </>
                      ) : (
                        <>
                          #{clip.jersey || athlete.jerseyNumber} – {clip.title}
                        </>
                      )}
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
